import { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster, toast } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PaymentModeProvider } from './contexts/PaymentModeContext';
import { ShadowModeProvider } from './contexts/ShadowModeContext';
import { HelmetProvider } from 'react-helmet-async';
import LegalReacceptanceModal from './components/LegalReacceptanceModal';
import ShadowModeBanner from './components/ShadowModeBanner';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ShadowEnter from './pages/ShadowEnter';
import RegisterPage from './pages/RegisterPage';
import SetPasswordPage from './pages/SetPasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ContactPage from './pages/ContactPage';
import TrustSafetyPage from './pages/TrustSafetyPage';
import RenterAgreementPage from './pages/RenterAgreementPage';
import CustomerDashboard from './pages/CustomerDashboard';
import HostDashboard from './pages/HostDashboard';
import HostAnalytics from './pages/HostAnalytics';
import VehicleAnalytics from './pages/VehicleAnalytics';
import VehicleSearch from './pages/VehicleSearch';
import VehicleDetails from './pages/VehicleDetails';
import VehicleManagement from './pages/VehicleManagement';
import VehicleForm from './pages/VehicleForm';
import BookingDetails from './pages/BookingDetails';
import ProfilePage from './pages/ProfilePage';
import VerificationPage from './pages/VerificationPage';
import HostRulesPage from './pages/HostRulesPage';
import AdminDashboard from './pages/AdminDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import PlatformOwnerDashboard from './pages/PlatformOwnerDashboard';
import PlatformOwnerPayouts from './pages/PlatformOwnerPayouts';
import PlatformOwnerVehicleManagement from './pages/PlatformOwnerVehicleManagement';
import RecoveryModeDashboard from './pages/RecoveryModeDashboard';
import FinancialEnforcementDashboard from './pages/FinancialEnforcementDashboard';
import OpsBriefing from './pages/OpsBriefing';
import AdminClaims from './pages/AdminClaims';
import AdminAgreements from './pages/AdminAgreements';
import AdminHostFinancials from './pages/AdminHostFinancials';
import DevDebugPanel from './pages/DevDebugPanel';
import PlatformCommandCentre from './pages/PlatformCommandCentre';
import LegalPage from './pages/LegalPage';
import HostInsuranceGuidance from './pages/HostInsuranceGuidance';
import NotificationsPage from './pages/NotificationsPage';
import MessagesPage from './pages/MessagesPage';
import ChatPage from './pages/ChatPage';
import SupportInboxPage from './pages/SupportInboxPage';
import HostPayouts from './pages/HostPayouts';
import HostStatements from './pages/HostStatements';
import HostBookings from './pages/HostBookings';
import HostExtensions from './pages/HostExtensions';
import HostAgreementPage from './pages/HostAgreementPage';
import HostAgreementsPage from './pages/HostAgreementsPage';
import HostAccountPage from './pages/HostAccountPage';
import HostReviewsPage from './pages/HostReviewsPage';
import HostClaimsPage from './pages/HostClaimsPage';
import HostAvailabilityPage from './pages/HostAvailabilityPage';
import HostManagePlanPage from './pages/HostManagePlanPage';
import HostFeaturePlaceholder from './pages/HostFeaturePlaceholder';
import HostGpsAlertsPage from './pages/HostGpsAlertsPage';
import HostAiAssistantPage from './pages/HostAiAssistantPage';
import HostSupportCenterPage from './pages/HostSupportCenterPage';
import MyAgreements from './pages/MyAgreements';
import AdminBookingEvidence from './pages/AdminBookingEvidence';
import StripeReturn from './pages/StripeReturn';
import GuestBookingSuccessPage from './pages/GuestBookingSuccessPage';
import StripeRefresh from './pages/StripeRefresh';
import HireHubProtection from './pages/HireHubProtection';
import SocialLinksSettings from './pages/SocialLinksSettings';
import EmailCentre from './pages/EmailCentre';
import BookingOversight from './pages/BookingOversight';
import ChargeCapsPage from './pages/ChargeCapsPage';
import PaymentAuthorizationsPage from './pages/PaymentAuthorizationsPage';
import SecuritySettings from './pages/SecuritySettings';
import SecurityActivity from './pages/SecurityActivity';
import TransferOwnership from './pages/TransferOwnership';
import CategoryLandingPage from './pages/CategoryLandingPage';

import './App.css';
import { getDashboardRoute } from './lib/navigation';

// Helper function to determine route from user object directly
const getRouteFromUser = (user) => {
  // Platform Owner has highest priority
  if (user.is_platform_owner || user.roles?.includes('platform_owner')) {
    return '/platform-owner';
  }
  // Super Admin
  if (user.roles?.includes('super_admin')) {
    return '/super-admin';
  }
  // Admin
  if (user.roles?.includes('admin')) {
    return '/admin';
  }
  // Host - check host_status === 'active' (canonical check)
  if (user.host_status === 'active') {
    return '/host';
  }
  // Default: Customer dashboard
  return '/dashboard';
};

// Backward-compatible redirect: /owner/* → /host/*
const OwnerToHostRedirect = () => {
  const location = useLocation();
  const newPath = location.pathname.replace(/^\/owner/, '/host');
  return <Navigate to={newPath + location.search + location.hash} replace />;
};

// Auth callback component for Google OAuth
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { processGoogleSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      processGoogleSession(sessionId)
        .then(async (result) => {
          // P1 FIX: result now contains is_new_user flag
          const isNewUser = result?.is_new_user;
          const sessionUser = result?.user || result;
          
          // Show appropriate welcome message based on new vs returning user
          if (isNewUser) {
            toast.success('Welcome to HireHub! Your account has been created.');
          } else {
            toast.success('Welcome back!');
          }
          
          // Fetch canonical user data from /api/auth/me to ensure host_status is present
          let canonicalUser = sessionUser;
          try {
            const apiUrl = window.location.origin || '';
            const storedToken = localStorage.getItem('token');
            const meResponse = await fetch(`${apiUrl}/api/auth/me`, {
              method: 'GET',
              headers: {
                'Cache-Control': 'no-cache',
                'Content-Type': 'application/json',
                'Authorization': storedToken ? `Bearer ${storedToken}` : ''
              },
              credentials: 'include'
            });
            if (meResponse.ok) {
              const meData = await meResponse.json();
              canonicalUser = meData;
            } else {
            }
          } catch (fetchError) {
          }
          
          // Route using canonical user data
          const route = getRouteFromUser(canonicalUser);
          navigate(route, { replace: true });
        })
        .catch((error) => {
          console.error('OAuth error:', error);
          navigate('/login', { replace: true });
        });
    } else {
      navigate('/login', { replace: true });
    }
  }, [location, navigate, processGoogleSession]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
  );
};

// Protected route wrapper
const ProtectedRoute = ({ children, requiredRoles = [], allowPasswordReset = false, requireHost = false, requireAdmin = false, blockPlatformOwner = false }) => {
  const { user, loading, requiresPasswordReset, isHost, isPlatformOwner } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // P0 SECURITY: Force password change if required (unless already on set-password page)
  // GUEST CHECKOUT EXCEPTION: Allow /verification and /guest-booking-success routes even with password_reset_required
  // This enables the booking-first flow where guest users verify identity and set password in-app
  const isVerificationRoute = location.pathname === '/verification';
  const isGuestSuccessRoute = location.pathname === '/guest-booking-success';
  if (requiresPasswordReset() && !allowPasswordReset && !isVerificationRoute && !isGuestSuccessRoute) {
    return <Navigate to="/set-password" replace />;
  }

  // P0 CRITICAL: Admin-only routes (platform owner / super_admin)
  if (requireAdmin) {
    if (isPlatformOwner() || user.roles?.includes('super_admin') || user.roles?.includes('admin')) {
      return children;
    }
    return <Navigate to="/dashboard" replace />;
  }

  // P0 CRITICAL: Host routes - BLOCK platform owner, require host_status=active
  if (requireHost) {
    // Platform owner can NEVER access host routes
    if (isPlatformOwner()) {
      return <Navigate to="/admin" replace />;
    }
    // Must be an active host
    if (isHost()) {
      return children;
    }
    // Not a host - redirect to customer dashboard
    return <Navigate to="/dashboard" replace />;
  }

  // P0 CRITICAL: Block platform owner from certain routes (like booking)
  if (blockPlatformOwner && isPlatformOwner()) {
    return <Navigate to="/admin" replace />;
  }

  // Platform owner has access to everything else
  if (isPlatformOwner()) {
    return children;
  }

  if (requiredRoles.length > 0 && !requiredRoles.some(role => user.roles?.includes(role))) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App router with session_id detection
const AppRouter = () => {
  const location = useLocation();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check for session_id in URL fragment SYNCHRONOUSLY during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/shadow/enter" element={<ShadowEnter />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/vehicles" element={<VehicleSearch />} />
      <Route path="/vehicles/:vehicleId" element={<VehicleDetails />} />
      <Route path="/vehicle/:vehicleId" element={<VehicleDetails />} /> {/* P1 SEO: Pretty URL route */}
      {/* Category Landing Pages */}
      <Route path="/cars" element={<CategoryLandingPage slug="cars" />} />
      <Route path="/motorcycles" element={<CategoryLandingPage slug="motorcycles" />} />
      <Route path="/boats" element={<CategoryLandingPage slug="boats" />} />
      <Route path="/jetskis" element={<CategoryLandingPage slug="jetskis" />} />
      <Route path="/trailers" element={<CategoryLandingPage slug="trailers" />} />
      <Route path="/ebikes" element={<CategoryLandingPage slug="ebikes" />} />
      <Route path="/electric-scooters" element={<CategoryLandingPage slug="electric-scooters" />} />
      <Route path="/mobility-scooters" element={<CategoryLandingPage slug="mobility-scooters" />} />
      <Route path="/specialty-assets" element={<CategoryLandingPage slug="specialty-assets" />} />
      <Route path="/legal/:pageType" element={<LegalPage />} />
      <Route path="/owners/insurance" element={<HostInsuranceGuidance />} />
      
      {/* Stripe Connect return routes - must be public for redirect handling */}
      <Route path="/stripe/return" element={<StripeReturn />} />
      <Route path="/stripe/refresh" element={<StripeRefresh />} />
      
      {/* P0 SECURITY: Mandatory Password Change */}
      <Route path="/set-password" element={
        <ProtectedRoute allowPasswordReset={true}>
          <SetPasswordPage />
        </ProtectedRoute>
      } />

      {/* Forgot/Reset Password (public routes) */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Guest Checkout Success (allows password_reset_required users to set their password) */}
      <Route path="/guest-booking-success" element={
        <ProtectedRoute allowPasswordReset={true}>
          <GuestBookingSuccessPage />
        </ProtectedRoute>
      } />
      
      {/* Contact Page (public) */}
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/trust-safety" element={<TrustSafetyPage />} />
      <Route path="/renter-agreement" element={<RenterAgreementPage />} />

      {/* Customer routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <CustomerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/agreements" element={
        <ProtectedRoute>
          <MyAgreements />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/verification" element={
        <ProtectedRoute>
          <VerificationPage />
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <NotificationsPage />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/messages/:threadId" element={
        <ProtectedRoute>
          <ChatPage />
        </ProtectedRoute>
      } />
      <Route path="/bookings/:bookingId" element={
        <ProtectedRoute>
          <BookingDetails />
        </ProtectedRoute>
      } />

      {/* Host Agreement Page - User can access without being a host yet */}
      <Route path="/host-agreement" element={
        <ProtectedRoute>
          <HostAgreementPage />
        </ProtectedRoute>
      } />

      {/* Host routes - P0 CRITICAL: Platform owner CANNOT access, requires host_status=active */}
      <Route path="/host" element={
        <ProtectedRoute requireHost={true}>
          <HostDashboard />
        </ProtectedRoute>
      } />
      <Route path="/host/vehicles" element={
        <ProtectedRoute requireHost={true}>
          <VehicleManagement />
        </ProtectedRoute>
      } />
      <Route path="/host/vehicles/new" element={
        <ProtectedRoute requireHost={true}>
          <VehicleForm />
        </ProtectedRoute>
      } />
      <Route path="/host/vehicles/:vehicleId/edit" element={
        <ProtectedRoute requireHost={true}>
          <VehicleForm />
        </ProtectedRoute>
      } />
      <Route path="/host/rules" element={
        <ProtectedRoute requireHost={true}>
          <HostRulesPage />
        </ProtectedRoute>
      } />
      <Route path="/host/analytics" element={
        <ProtectedRoute requireHost={true}>
          <HostAnalytics />
        </ProtectedRoute>
      } />
      <Route path="/host/analytics/vehicle/:vehicleId" element={
        <ProtectedRoute requireHost={true}>
          <VehicleAnalytics />
        </ProtectedRoute>
      } />
      <Route path="/host/payouts" element={
        <ProtectedRoute requireHost={true}>
          <HostPayouts />
        </ProtectedRoute>
      } />
      <Route path="/host/statements" element={
        <ProtectedRoute requireHost={true}>
          <HostStatements />
        </ProtectedRoute>
      } />
      {/* Host Bookings - bookings for host's vehicles */}
      <Route path="/host/bookings" element={
        <ProtectedRoute requireHost={true}>
          <HostBookings />
        </ProtectedRoute>
      } />
      
      {/* Host Agreements - view signed agreements */}
      <Route path="/host/agreements" element={
        <ProtectedRoute requireHost={true}>
          <HostAgreementsPage />
        </ProtectedRoute>
      } />
      
      {/* Host Account - unified profile/settings */}
      <Route path="/host/account" element={
        <ProtectedRoute requireHost={true}>
          <HostAccountPage />
        </ProtectedRoute>
      } />
      
      {/* Host Reviews - view reviews from renters */}
      <Route path="/host/reviews" element={
        <ProtectedRoute requireHost={true}>
          <HostReviewsPage />
        </ProtectedRoute>
      } />
      
      {/* Host Claims - view and track claims */}
      <Route path="/host/claims" element={
        <ProtectedRoute requireHost={true}>
          <HostClaimsPage />
        </ProtectedRoute>
      } />
      
      {/* Host Extensions - review and manage extension requests */}
      <Route path="/host/extensions" element={
        <ProtectedRoute requireHost={true}>
          <HostExtensions />
        </ProtectedRoute>
      } />
      
      {/* Host Availability - manage fleet availability calendar */}
      <Route path="/host/calendar" element={
        <ProtectedRoute requireHost={true}>
          <HostAvailabilityPage />
        </ProtectedRoute>
      } />
      
      {/* Host Manage Plan - tier comparison and upgrade interest */}
      <Route path="/host/plan" element={
        <ProtectedRoute requireHost={true}>
          <HostManagePlanPage />
        </ProtectedRoute>
      } />
      
      {/* Host Tier Feature Placeholders */}
      <Route path="/host/gps" element={<ProtectedRoute requireHost={true}><HostGpsAlertsPage /></ProtectedRoute>} />
      <Route path="/host/boost" element={<ProtectedRoute requireHost={true}><HostFeaturePlaceholder featureKey="boost" /></ProtectedRoute>} />
      <Route path="/host/ai-assistant" element={<ProtectedRoute requireHost={true}><HostAiAssistantPage /></ProtectedRoute>} />
      <Route path="/host/priority-support" element={<ProtectedRoute requireHost={true}><HostSupportCenterPage /></ProtectedRoute>} />
      <Route path="/host/insurance-perks" element={<ProtectedRoute requireHost={true}><HostFeaturePlaceholder featureKey="insurance-perks" /></ProtectedRoute>} />
      
      {/* HireHub Protection - host trust layer page (accessible to all authenticated users) */}
      <Route path="/host/protection" element={
        <ProtectedRoute>
          <HireHubProtection />
        </ProtectedRoute>
      } />

      {/* Backward-compatible redirects: /owner/* → /host/* */}
      <Route path="/owner" element={<Navigate to="/host" replace />} />
      <Route path="/owner/*" element={<OwnerToHostRedirect />} />

      {/* Admin routes - P0 CRITICAL: requireAdmin for proper access control */}
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin={true}>
          <AdminDashboard />
        </ProtectedRoute>
      } />

      {/* Super Admin routes */}
      <Route path="/super-admin" element={
        <ProtectedRoute requiredRoles={['super_admin', 'platform_owner']}>
          <SuperAdminDashboard />
        </ProtectedRoute>
      } />
      
      {/* Platform Owner routes - HIGHEST PRIVILEGE */}
      <Route path="/platform-owner" element={
        <ProtectedRoute requiredRoles={['platform_owner']}>
          <PlatformOwnerDashboard />
        </ProtectedRoute>
      } />
      
      {/* Platform Owner Payouts - PLATFORM OWNER ONLY */}
      <Route path="/platform-owner/payouts" element={
        <ProtectedRoute requiredRoles={['platform_owner']}>
          <PlatformOwnerPayouts />
        </ProtectedRoute>
      } />
      
      {/* Platform Owner Vehicle Management - PLATFORM OWNER ONLY */}
      <Route path="/platform-owner/vehicles" element={
        <ProtectedRoute requiredRoles={['platform_owner']}>
          <PlatformOwnerVehicleManagement />
        </ProtectedRoute>
      } />
      
      {/* Recovery Mode Dashboard - PLATFORM OWNER ONLY */}
      <Route path="/platform-owner/recovery" element={
        <ProtectedRoute requiredRoles={['platform_owner']}>
          <RecoveryModeDashboard />
        </ProtectedRoute>
      } />
      
      {/* Ops Briefing - PLATFORM OWNER / ADMIN ONLY */}
      <Route path="/platform-owner/ops-briefing" element={
        <ProtectedRoute requiredRoles={['platform_owner', 'admin', 'super_admin']}>
          <OpsBriefing />
        </ProtectedRoute>
      } />
      
      <Route path="/platform-owner/recovery/:vehicleId" element={
        <ProtectedRoute requiredRoles={['platform_owner']}>
          <RecoveryModeDashboard />
        </ProtectedRoute>
      } />
      
      {/* Support Inbox - Platform Owner ONLY */}
      <Route path="/support-inbox" element={
        <ProtectedRoute requiredRoles={['platform_owner']}>
          <SupportInboxPage />
        </ProtectedRoute>
      } />
      
      {/* Financial Enforcement Dashboard - Platform Owner ONLY */}
      <Route path="/financial-enforcement" element={
        <ProtectedRoute requiredRoles={['platform_owner']}>
          <FinancialEnforcementDashboard />
        </ProtectedRoute>
      } />
      
      {/* Claims Management - Platform Owner ONLY */}
      <Route path="/admin/claims" element={
        <ProtectedRoute requiredRoles={['platform_owner']}>
          <AdminClaims />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/agreements" element={
        <ProtectedRoute requiredRoles={['platform_owner']}>
          <AdminAgreements />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/host-financials" element={
        <ProtectedRoute requiredRoles={['platform_owner']}>
          <AdminHostFinancials />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/host-financials/:userId" element={
        <ProtectedRoute requiredRoles={['platform_owner']}>
          <AdminHostFinancials />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/bookings/:bookingId/evidence" element={
        <ProtectedRoute requiredRoles={['platform_owner']}>
          <AdminBookingEvidence />
        </ProtectedRoute>
      } />
      
      {/* Platform Owner Social Links Settings */}
      <Route path="/platform-owner/social-links" element={
        <ProtectedRoute requiredRoles={['platform_owner']}>
          <SocialLinksSettings />
        </ProtectedRoute>
      } />
      
      {/* Platform Owner Email Centre */}
      <Route path="/platform-owner/email-centre" element={
        <ProtectedRoute requiredRoles={['platform_owner']}>
          <EmailCentre />
        </ProtectedRoute>
      } />
      
      {/* Platform Owner Booking Oversight */}
      <Route path="/platform-owner/bookings" element={
        <ProtectedRoute requiredRoles={['platform_owner']}>
          <BookingOversight />
        </ProtectedRoute>
      } />

      {/* Platform Owner Charge Caps */}
      <Route path="/platform-owner/charge-caps" element={
        <ProtectedRoute requiredRoles={['platform_owner']}>
          <ChargeCapsPage />
        </ProtectedRoute>
      } />

      {/* Platform Owner Payment Authorizations */}
      <Route path="/platform-owner/payment-authorizations" element={
        <ProtectedRoute requiredRoles={['platform_owner']}>
          <PaymentAuthorizationsPage />
        </ProtectedRoute>
      } />

      {/* Platform Owner / Admin Security Settings */}
      <Route path="/platform-owner/security" element={
        <ProtectedRoute requiredRoles={['platform_owner', 'super_admin', 'admin']}>
          <SecuritySettings />
        </ProtectedRoute>
      } />

      {/* Platform Owner Security Activity */}
      <Route path="/platform-owner/security-activity" element={
        <ProtectedRoute requiredRoles={['platform_owner']}>
          <SecurityActivity />
        </ProtectedRoute>
      } />

      {/* Platform Owner Transfer Ownership */}
      <Route path="/platform-owner/transfer-ownership" element={
        <ProtectedRoute requiredRoles={['platform_owner']}>
          <TransferOwnership />
        </ProtectedRoute>
      } />
      
      {/* DEV DEBUG - Platform Owner and Super Admin */}
      <Route path="/dev-debug" element={
        <ProtectedRoute requiredRoles={['super_admin', 'platform_owner']}>
          <PlatformCommandCentre />
        </ProtectedRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// P0 CRITICAL: Global Legal Compliance Check
// Shows modal for users who haven't accepted Terms/Privacy
// Platform Owner is EXEMPT from this check
// DEV bypass: Session-level bypass for testing (Platform Owner only)
const DEV_LEGAL_BYPASS_KEY = 'dev_legal_bypass_session';

const GlobalLegalCheck = () => {
  const { user, legalCompliance, needsLegalReacceptance, isPlatformOperator, refreshLegalCompliance, refreshUser } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const location = useLocation();
  
  useEffect(() => {
    // Skip if not logged in, still loading, or on legal/public pages
    if (!user) return;
    
    // P3 CRITICAL: Platform Operators (admin/super_admin) are EXEMPT from legal acceptance
    // They are operators of the platform, not booking participants
    // This means:
    // - No Terms/Privacy acceptance gate for admin dashboards
    // - No host/renter legal modal blocking admin login
    // - No booking-participant legal checks applied to admin or super_admin roles
    if (isPlatformOperator()) return;
    
    // DEV bypass: Check if session bypass is set (Platform Owner testing)
    // SAFETY: Only functional in non-production environments
    if (process.env.NODE_ENV !== 'production') {
      const devBypass = sessionStorage.getItem(DEV_LEGAL_BYPASS_KEY);
      if (devBypass === 'true') return;
    }
    
    // Skip on login/register/legal pages
    const exemptPaths = ['/login', '/register', '/legal', '/set-password', '/auth/callback'];
    if (exemptPaths.some(p => location.pathname.startsWith(p))) return;
    
    // Check if legal acceptance is needed
    if (needsLegalReacceptance()) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [user, legalCompliance, location.pathname, isPlatformOperator, needsLegalReacceptance]);
  
  const handleAccepted = async () => {
    setShowModal(false);
    // Refresh compliance status
    await refreshLegalCompliance();
    await refreshUser();
  };
  
  return (
    <LegalReacceptanceModal 
      isOpen={showModal} 
      onAccepted={handleAccepted}
    />
  );
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <PaymentModeProvider>
          <ShadowModeProvider>
            <BrowserRouter>
              <ScrollToTop />
              <ShadowModeBanner />
              <ErrorBoundary>
                <AppRouter />
              </ErrorBoundary>
              <GlobalLegalCheck />
              <Toaster position="top-right" richColors />
            </BrowserRouter>
          </ShadowModeProvider>
        </PaymentModeProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
